import React, { JSX, useContext, useEffect, useState } from "react";
import { cloneDeep, isEqual, isNil } from "lodash";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters";
import { BenchmarkingLineOfBusinessEnum, BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { NetworkComparisonFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared";
import { getPcaLineOfBusiness } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/utility";
import { QHPFiltersContext, QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { EffectivenessEfficiencyValueEnum } from "@app/projects/servicing-provider";
import { DropdownItem, MultiselectDropdown } from "@app/shared/checkbox-dropdown";
import { QESButton, QESSelect } from "@app/shared/qes-standards";

const NetworkComparisonFilters = (): JSX.Element => {
  const [hasFilterData, setHasFilterData] = useState<boolean>(false);

  const benchmarkingContext = useContext(BenchmarkingContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const networkComparisonFiltersContext = useContext(NetworkComparisonFiltersContext);
  const qhpFiltersContext = useContext(QHPFiltersContext);

  useEffect(() => {
    if (!!benchmarkingFiltersContext.displayedNetworkFilters.length && !!benchmarkingFiltersContext.specialties.length) {
      setHasFilterData(true);
    } else {
      setHasFilterData(false);
    }
  }, [benchmarkingFiltersContext.displayedNetworkFilters, benchmarkingFiltersContext.specialties]);

  const onApplyNetworkComparisonFiltersClick = () => {
    const displayedFilters: NetworkComparisonFiltersPayload = networkComparisonFiltersContext.getNetworkComparisonFiltersPayload();
    networkComparisonFiltersContext.setDisplayedNetworkComparisonFilters(displayedFilters);
    networkComparisonFiltersContext.setOriginalDisplayedNetworkComparisonFilters(displayedFilters);
    benchmarkingContext.onApplyNetworkComparisonFiltersClick(cloneDeep(displayedFilters));
  }

  const clearDisabled = (): boolean => {
    // clear should be disabled unless
    // 1.) there is filter data
    // 2.) at least one of the dropdowns has different settings than the default
    if (hasFilterData && (
      networkComparisonFiltersContext.selectedNetwork !== networkComparisonFiltersContext.originalSelectedNetwork ||
      networkComparisonFiltersContext.selectedSpecialty !== 0 ||
      networkComparisonFiltersContext.selectedPcaLineOfBusiness !== getPcaLineOfBusiness(benchmarkingFiltersContext.selectedLineOfBusiness) ||
      networkComparisonFiltersContext.selectedVolumeIndicators.length !== networkComparisonFiltersContext.mappedVolumeIndicators.length ||
      networkComparisonFiltersContext.selectedEffectivenessValues.length !== networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues.length ||
      networkComparisonFiltersContext.selectedEfficiencyValues.length !== networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues.length
    )) {
      return false;
    }

    return true;
  }

  const applyDisabled = (): boolean => {
    const volumeIndicatorsEqual = isEqual(
      networkComparisonFiltersContext.displayedNetworkComparisonFilters?.volumeIndicators.sort((a, b) => a.localeCompare(b)),
      networkComparisonFiltersContext.selectedVolumeIndicators.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const effectivenessEqual = isEqual(
      networkComparisonFiltersContext.displayedNetworkComparisonFilters?.effectivenessRanks.sort((a, b) => a.localeCompare(b)),
      networkComparisonFiltersContext.selectedEffectivenessValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const efficiencyEqual = isEqual(
      networkComparisonFiltersContext.displayedNetworkComparisonFilters?.efficiencyRanks.sort((a, b) => a.localeCompare(b)),
      networkComparisonFiltersContext.selectedEfficiencyValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    // apply should be disabled unless
    // 1.) there is filter data
    // 2.) at least one of the dropdown options is different than the displayed options
    // 3.) the minimum amount of items for each dropdown
    if (hasFilterData && (
      networkComparisonFiltersContext.selectedNetwork?.contractId !== networkComparisonFiltersContext.displayedNetworkComparisonFilters?.primaryNetwork?.contractId ||
        networkComparisonFiltersContext.selectedSpecialty !== networkComparisonFiltersContext.displayedNetworkComparisonFilters?.specialtyGroupId ||
        networkComparisonFiltersContext.selectedPcaLineOfBusiness !== networkComparisonFiltersContext.displayedNetworkComparisonFilters?.pcaLineOfBusiness ||
        !volumeIndicatorsEqual ||
        !effectivenessEqual ||
        !efficiencyEqual
      ) && (
        !!networkComparisonFiltersContext.selectedNetwork &&
        !isNil(networkComparisonFiltersContext.selectedSpecialty) &&
        !!networkComparisonFiltersContext.selectedPcaLineOfBusiness &&
        !!networkComparisonFiltersContext.selectedVolumeIndicators.length &&
        !!networkComparisonFiltersContext.selectedEffectivenessValues.length &&
        !!networkComparisonFiltersContext.selectedEfficiencyValues.length
      )) {
      return false;
    }

    return true;
  }

  const getPrimaryNetworkDisplayValue = (network: BenchmarkingNetworkType): string => {
    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Medicare:
        return network.contractId;
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        const qhpNetwork: QHPNetwork = network as QHPNetwork;
        const yourNetworks: QHPNetwork[] = qhpFiltersContext.parentOrganizations.find(x => x.parentOrganizationName === "Your QES Networks")?.hContracts;
        const isYourQesNetwork = yourNetworks?.map(x => x.contractId).includes(qhpNetwork.contractId);

        let networkString = `${qhpNetwork.contractId} - ${isYourQesNetwork? "Your QES Networks" : qhpNetwork.networkName}`;

        // if the combination of the contract id, network name, and parent organization
        // exceeds 50 characters, then truncate the text and append an ellipsis
        if (networkString.length > 50) {
          networkString = `${networkString.substring(0, 50)}...`;
        }

        return networkString;
      default:
        return;
    }
  }

  const onPrimaryNetworkChange = (contractId: string): void => {
    const network: BenchmarkingNetworkType = benchmarkingFiltersContext.displayedNetworkFilters.find(x => x.contractId === contractId);
    if (!!network) {
      networkComparisonFiltersContext.setSelectedNetwork(network);
    }
  }

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <div className="card card-static">
            <div className="card-body">
              <div className="row">
                <div className="col-12">
                  <label className="label-m mb-2">
                    Network Comparison
                  </label>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <label className="copy-text-default mb-3">
                    You may customize the filters below to meet your specific requirements. Please select a single specialty and click &#34;Apply&#34; to enable click-through on the graphs for more detailed information.
                  </label>
                </div>
              </div>
              <div className="row">
                <div className="col-md-10">
                  <div className="row">
                    <div className="col-md-2">
                      <QESSelect
                        disabled={!hasFilterData}
                        testId="network-benchmarking-dashboard__network-comparison-filters--primary-network"
                        label="Primary Network"
                        onChange={(e) => onPrimaryNetworkChange(e.target.value)}
                        value={networkComparisonFiltersContext.selectedNetwork?.contractId || ""}
                      >
                        {benchmarkingFiltersContext.displayedNetworkFilters.map((network, index) => (
                          <option
                            key={index}
                            value={network.contractId}
                          >
                            {getPrimaryNetworkDisplayValue(network)}
                          </option>
                        ))}
                      </QESSelect>
                    </div>
                    <div className="col-md-2">
                      <QESSelect
                        disabled={!hasFilterData}
                        testId="network-benchmarking-dashboard__network-comparison-filters--specialty"
                        label="Specialty"
                        onChange={(e) => networkComparisonFiltersContext.setSelectedSpecialty(parseInt(e.target.value))}
                        value={networkComparisonFiltersContext.selectedSpecialty?.toString() || ""}
                      >
                        {benchmarkingFiltersContext.specialties.map((specialty, index) => (
                          <option
                            key={index}
                            value={specialty.specialtyGroupId}
                          >
                            {specialty.specialtyGroupId !== 0 && `${specialty.specialtyGroupCode} - `}{specialty.specialtyGroupName}
                          </option>
                        ))}
                      </QESSelect>
                    </div>
                    <div className="col-md-2">
                      <QESSelect
                        disabled={!hasFilterData}
                        testId="network-benchmarking-dashboard__network-comparison-filters--pca-line-of-business"
                        label="PCA Line of Business"
                        onChange={(e) => networkComparisonFiltersContext.setSelectedPcaLineOfBusiness(e.target.value as ClaimsActivityLineOfBusinessTypeEnum)}
                        value={networkComparisonFiltersContext.selectedPcaLineOfBusiness || ""}
                      >
                        {Object.values(ClaimsActivityLineOfBusinessTypeEnum).filter(v => v !== ClaimsActivityLineOfBusinessTypeEnum.VA && v !== ClaimsActivityLineOfBusinessTypeEnum.TRICARE).map((lineOfBusiness, index) => (
                          <option
                            key={index}
                            value={lineOfBusiness}
                          >
                            {lineOfBusiness}
                          </option>
                        ))}
                      </QESSelect>
                    </div>
                    <div className="col-md-2">
                      <MultiselectDropdown
                        containerClassName="d-block"
                        customSelectInputText={(!!networkComparisonFiltersContext.mappedVolumeIndicators.length && networkComparisonFiltersContext.mappedVolumeIndicators.length === networkComparisonFiltersContext.selectedVolumeIndicators.length) ? "All" : null}
                        disabled={!hasFilterData}
                        items={networkComparisonFiltersContext.mappedVolumeIndicators}
                        onSelectionChanged={(selectedVolumeIndicators: DropdownItem<ClaimsActivityVolumeLabelEnum>[]) => { networkComparisonFiltersContext.setSelectedVolumeIndicators(selectedVolumeIndicators) }}
                        label="Volume Indicator"
                        selectedItems={networkComparisonFiltersContext.selectedVolumeIndicators}
                        testId="network-benchmarking-dashboard__network-comparison-filters--volume-indicator"
                      />
                    </div>
                    <div className="col-md-2">
                      <MultiselectDropdown
                        containerClassName="d-block"
                        customSelectInputText={(!!networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues.length && networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues.length === networkComparisonFiltersContext.selectedEffectivenessValues.length) ? "All" : null}
                        disabled={!hasFilterData}
                        items={networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues}
                        onSelectionChanged={(selectedValues: DropdownItem<EffectivenessEfficiencyValueEnum>[]) => { networkComparisonFiltersContext.setSelectedEffectivenessValues(selectedValues) }}
                        label="Effectiveness"
                        selectedItems={networkComparisonFiltersContext.selectedEffectivenessValues}
                        testId="network-benchmarking-dashboard__network-comparison-filters--effectiveness"
                      />
                    </div>
                    <div className="col-md-2">
                      <MultiselectDropdown
                        containerClassName="d-block"
                        customSelectInputText={(!!networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues.length && networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues.length === networkComparisonFiltersContext.selectedEfficiencyValues.length) ? "All" : null}
                        disabled={!hasFilterData}
                        items={networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues}
                        onSelectionChanged={(selectedValues: DropdownItem<EffectivenessEfficiencyValueEnum>[]) => { networkComparisonFiltersContext.setSelectedEfficiencyValues(selectedValues) }}
                        label="Efficiency"
                        selectedItems={networkComparisonFiltersContext.selectedEfficiencyValues}
                        testId="network-benchmarking-dashboard__network-comparison-filters--efficiency"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <div>
                    <QESButton
                      disabled={applyDisabled()}
                      qesTestId="network-benchmarking-dashboard__network-comparison-filters--apply"
                      qesType="secondary"
                      onClick={() => onApplyNetworkComparisonFiltersClick()}
                      className="pb-0"
                    >
                      Apply
                    </QESButton>

                    <QESButton
                      disabled={clearDisabled()}
                      qesTestId="network-benchmarking-dashboard__network-comparison-filters--clear"
                      qesType="secondary-danger"
                      onClick={() => networkComparisonFiltersContext.onClearNetworkComparisonFiltersClick()}
                      className="pb-0"
                    >
                      Clear
                    </QESButton>
                  </div>
                </div>
              </div>
              {!hasFilterData && (
                <div className="row">
                  <div className="col-12">
                    <p className="copy-text-default font-italic mb-0 mt-3">
                      No results currently displayed. Input search parameters to get results.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>

  );
}

export default NetworkComparisonFilters;